import Hls, { HlsConfig, MediaPlaylist } from 'hls.js';
import { SmartLib } from '@broadpeak/smartlib';
import {
  Drm,
  PlayerEventCallback,
  BasePlayer,
  SubtitleTrack,
} from './BasePlayer';
import {
  parseHtmlDurationRange,
  Logger,
  uInt8ArrayToString,
  waitFor,
  base64EncodeUint8Array,
  base64DecodeUint8Array,
} from './util';
import * as dashjs from 'dashjs';

let html5PlayerInitialized = false;
let smartLibInitialized = false;

class Html5Player implements BasePlayer {
  private player?: HTMLVideoElement;
  private isPaused: boolean = false;
  private readonly debug: boolean = true;
  private buffered: number = 0;
  private duration: number = 0;
  private videoWidth: number = 0;
  private videoHeight: number = 0;
  private audioTracks: MediaPlaylist[] = [];
  private subtitleTracks: MediaPlaylist[] = [];
  private readonly playerId: string = 'web_player';
  private eventCallback?: PlayerEventCallback;
  private hlsSourceUrl: string | null = null;
  private currentAudioTrackIndex: number | null = null;
  private currentHlsInstance: Hls | null = null;
  private currentDashInstance: dashjs.MediaPlayerClass | null = null;
  private hasBufferAppendingError: boolean = false;
  private isRecoveringFromCorruptedChunk: boolean = false;
  private recoveryAttempts: number = 0;
  private readonly maxRecoveryAttempts: number = 3;
  private session: any | null = null;
  private sourceId: number = 0;
  private listenersAttachedTo: HTMLVideoElement | null = null;
  private recoveryTimerHandle: ReturnType<typeof setTimeout> | null = null;
  private recoveryResumeTimerHandle: ReturnType<typeof setTimeout> | null =
    null;

  private ttmlRenderingDiv: HTMLDivElement | null = null;

  private encryptedEventListener?: (
    this: HTMLVideoElement,
    ev: MediaEncryptedEvent
  ) => any;

  private readonly isInitializingMediaKeys: boolean = false;
  private isDestroyed: boolean = false;
  private isPlayerReady: boolean = false;
  private pendingVideoElement: HTMLVideoElement | null = null;

  private readonly logger: Logger = new Logger('HTML5Player');

  isInitialized(): boolean {
    return html5PlayerInitialized;
  }

  viewElement(): HTMLElement {
    this.logger.log('[Player viewElement]');
    const playerWrapper = document.createElement('div');
    playerWrapper.style.width = '100%';
    playerWrapper.style.height = '100%';

    const videoElement = document.createElement('video');
    videoElement.id = this.playerId;
    videoElement.style.width = '100%';
    videoElement.style.height = '100%';
    this.logger.log(`creating video element: ${videoElement.id}`);

    videoElement.autoplay = true;

    playerWrapper.appendChild(videoElement);

    const ttmlDiv = document.createElement('div');
    ttmlDiv.id = 'ttml-rendering-div';
    ttmlDiv.style.position = 'absolute';
    ttmlDiv.style.top = '0';
    ttmlDiv.style.left = '0';
    ttmlDiv.style.width = '100%';
    ttmlDiv.style.height = '100%';
    ttmlDiv.style.pointerEvents = 'none';
    ttmlDiv.style.zIndex = '1';
    playerWrapper.style.position = 'relative';
    playerWrapper.appendChild(ttmlDiv);
    this.ttmlRenderingDiv = ttmlDiv;

    const ttmlStyle = document.createElement('style');
    ttmlStyle.textContent = `
      #ttml-rendering-div div[style*="position: absolute"] {
        left: 0 !important;
        width: 100% !important;
      }
      #ttml-rendering-div div[style*="flex-direction: column"],
      #ttml-rendering-div div[style*="flex-direction:column"] {
        align-items: center !important;
      }
      #ttml-rendering-div div[style*="flex-direction: row"],
      #ttml-rendering-div div[style*="flex-direction:row"] {
        justify-content: center !important;
      }
      #ttml-rendering-div div[style*="display: flex"]:not([style*="flex-direction"]),
      #ttml-rendering-div div[style*="display:flex"]:not([style*="flex-direction"]) {
        justify-content: center !important;
      }
      #ttml-rendering-div * {
        font-family: Arial, Helvetica, sans-serif !important;
        text-align: center !important;
        color: white !important;
        background: none !important;
        background-color: transparent !important;
        text-shadow: 0 1px 3px rgba(0,0,0,0.85), 0 0 8px rgba(0,0,0,0.5) !important;
      }
    `;
    document.head.appendChild(ttmlStyle);

    // Store reference so init() doesn't depend on DOM lookup timing
    this.pendingVideoElement = videoElement;

    playerWrapper.addEventListener('contextmenu', (event) =>
      event.preventDefault()
    );

    this.logger.log('returning html5 player');
    return playerWrapper;
  }

  async init(): Promise<void> {
    this.logger.log('[Player init]');
    if (html5PlayerInitialized && this.player != null) {
      this.logger.log('init: Player already initialized, no need to call init');
      return;
    }

    // Try stored reference first, then DOM lookup, then wait for DOM
    let playerElement: HTMLVideoElement | null =
      this.pendingVideoElement ??
      (document.getElementById(this.playerId) as HTMLVideoElement | null);

    if (!(playerElement instanceof HTMLVideoElement)) {
      this.logger.log('player not in DOM yet, waiting...');
      // Wait up to 2 seconds for Flutter to insert the element
      for (let i = 0; i < 20; i++) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        playerElement = document.getElementById(
          this.playerId
        ) as HTMLVideoElement | null;
        if (playerElement instanceof HTMLVideoElement) break;
        // Also re-check the stored reference
        if (this.pendingVideoElement != null) {
          playerElement = this.pendingVideoElement;
          break;
        }
      }
    }

    if (!(playerElement instanceof HTMLVideoElement)) {
      this.logger.error('Error: player id not found at init() after waiting');
      return;
    }

    this.isDestroyed = false;
    this.isPlayerReady = false;
    this.player = playerElement;
    this.player.controls = false;
    this.addPlayerListeners();
    this.logger.log('found player id at init');
    html5PlayerInitialized = true;
    this.isPlayerReady = true;
    if (!smartLibInitialized) {
      SmartLib.getInstance().init('', '', '*');
      smartLibInitialized = true;
      this.logger.log('SmartLib plugin initialized');
    } else {
      this.logger.log('SmartLib plugin already initialized, skipping');
    }
  }

  addPlayerListeners(): void {
    this.logger.log('[Player addPlayerListeners2]');
    if (this.player == null) {
      this.logger.log('addPlayerListeners: player not initialized');
      return;
    }
    if (this.listenersAttachedTo === this.player) {
      this.logger.log(
        'addPlayerListeners: already attached to this element, skipping'
      );
      return;
    }

    this.player.addEventListener('error', (event) => {
      this.logger.log(event);
      this.eventCallback?.({ key: this.playerId, type: 'error' });
      this.stopSession();
    });
    this.player.addEventListener('play', (event) => {
      this.logger.log(event);
      this.eventCallback?.({ key: this.playerId, type: 'play' });
    });
    this.player.addEventListener('ended', (event) => {
      this.logger.log(event);
      this.eventCallback?.({ key: this.playerId, type: 'completed' });
    });
    this.player.addEventListener('durationchange', (event) => {
      this.logger.log(event);
      this.duration = 0;
      this.videoHeight = 100;
      this.videoWidth = 200;
      this.buffered = 10;
    });

    this.player.addEventListener('pause', (event) => {
      this.logger.log(event);
      this.eventCallback?.({ key: this.playerId, type: 'pause' });
    });

    this.player?.addEventListener('waiting', () => {
      this.eventCallback?.({ key: this.playerId, type: 'bufferingStart' });
    });

    this.player?.addEventListener('canplaythrough', () => {
      this.eventCallback?.({ key: this.playerId, type: 'bufferingEnd' });
    });
    this.player?.addEventListener('progress', (event) => {
      this.logger.log(event);
      if (this.player == null) {
        return;
      }
      this.logger.log(`duration time: ${this.duration}`);
      const buffered = this.player.buffered;
      const bufferedRanges = parseHtmlDurationRange(buffered);
      this.logger.log(`Buffered Ranges: ${JSON.stringify(bufferedRanges)}`);
      if (bufferedRanges.length > 0) {
        this.eventCallback?.({
          key: this.playerId,
          type: 'bufferingUpdate',
          buffered: bufferedRanges,
          duration: this.getDurationInMilliseconds(),
        });
      }
    });
    this.player.addEventListener('loadedmetadata', (event) => {
      if (this.player == null) {
        return;
      }
      this.videoWidth = this.player.videoWidth;
      this.videoHeight = this.player.videoHeight;
      this.duration = this.getDurationInMilliseconds();
      this.logger.log(
        `html5Player: loadedmetadata duration: ${this.duration} width: ${this.videoWidth} height:  ${this.videoHeight}`
      );
      if (this.debug) {
        this.logger.log(event);
      }

      this.eventCallback?.({
        key: this.playerId,
        type: 'initialized',
        duration: this.duration,
        size: {
          width: this.videoWidth,
          height: this.videoHeight,
        },
      });
    });
    this.player.addEventListener('resize', (event) => {
      this.logger.log(event);
      if (this.player == null) {
        return;
      }

      this.eventCallback?.({
        key: this.playerId,
        type: 'updateVideoSize',
        bitrate: -1,
        size: {
          width: this.player.videoWidth,
          height: this.player.videoHeight,
        },
      });
    });
    this.listenersAttachedTo = this.player;
  }

  async destroy(): Promise<void> {
    this.logger.log('Player destroy]');

    this.stopSession();
    if (this.player == null) {
      this.logger.log('destroy: Player not initialized');
      return;
    }
    this.logger.log('calling destroy');
    this.player?.pause();
    this.player.removeAttribute('src'); // empty source

    // are bellow lines necessary?
    this.player.load();
    this.listenersAttachedTo = null;
    html5PlayerInitialized = false;
  }

  private getSourceType(url: string): string {
    if (url.includes('.mp4')) {
      return 'video/mp4';
    }
    return 'application/x-mpegurl';
  }

  stopSession(): void {
    if (this.session != null) {
      this.session.stopStreamingSession();
      this.session = null;
    }
  }

  clearCurrentSource(): void {
    this.logger.log('clearCurrentSource');

    this.stopSession();
    this.duration = 0;
    this.videoHeight = 0;
    this.videoHeight = 0;
    this.subtitleTracks = [];
    this.audioTracks = [];
    this.recoveryAttempts = 0;
    this.isRecoveringFromCorruptedChunk = false;

    if (this.recoveryTimerHandle != null) {
      clearTimeout(this.recoveryTimerHandle);
      this.recoveryTimerHandle = null;
    }

    if (this.recoveryResumeTimerHandle != null) {
      clearTimeout(this.recoveryResumeTimerHandle);
      this.recoveryResumeTimerHandle = null;
    }

    if (this.player == null) {
      return;
    }

    try {
      while (this.player.firstChild != null) {
        this.player.removeChild(this.player.firstChild);
      }
    } catch (e) {
      this.logger.error('Error removing player children:', e);
    }

    try {
      if (this.currentHlsInstance != null) {
        const hlsToDestroy = this.currentHlsInstance;
        this.currentHlsInstance = null;
        try {
          hlsToDestroy.detachMedia();
        } catch (e) {
          this.logger.error(
            'Error detaching HLS media in clearCurrentSource:',
            e
          );
        }
        setTimeout(() => {
          try {
            hlsToDestroy.destroy();
          } catch (e) {
            // Ignore - instance may already be destroyed
          }
        }, 0);
      }
    } catch (e) {
      this.logger.error(
        'Error destroying HLS instance in clearCurrentSource:',
        e
      );
      this.currentHlsInstance = null;
    }

    try {
      this.player.pause();
      this.player.removeAttribute('src');
      (this.player as HTMLVideoElement & { srcObject?: unknown }).srcObject =
        null;
      this.player.load();
    } catch (e) {
      this.logger.error(
        'Error resetting media element in clearCurrentSource:',
        e
      );
    }

    try {
      if (this.currentDashInstance != null) {
        this.currentDashInstance.reset();
        this.currentDashInstance = null;
      }
    } catch (e) {
      this.logger.error(
        'Error resetting DASH instance in clearCurrentSource:',
        e
      );
      this.currentDashInstance = null;
    }

    try {
      if (this.encryptedEventListener != null) {
        this.logger.log('removing encrypted event listener');
        this.player.removeEventListener(
          'encrypted',
          this.encryptedEventListener
        );
      }
    } catch (e) {
      this.logger.error('Error removing encrypted event listener:', e);
    }
  }

  async playClearStream(url: string): Promise<void> {
    if (this.player == null) {
      return;
    }

    try {
      this.logger.log('native html5');

      const source = document.createElement('source');
      source.src = url;
      source.type = this.getSourceType(url);
      this.player.appendChild(source);
      this.player.load();
    } catch (e) {
      this.logger.error('error at playClearStream', e);
    }
  }

  getLicenseUrl(drm: Drm): string {
    const authorizationHeader = drm.headers as { Authorization?: string };
    const jwtToken = (authorizationHeader.Authorization ?? '').replace(
      'Bearer ',
      ''
    );
    return `${drm.data.licenseUrl}${
      drm.data.licenseUrl.includes('?') ? `&jwt=${jwtToken}` : ''
    }
    `;
  }

  private bypassAuthentication(licenseUrl: string): boolean {
    return (
      licenseUrl.includes('lic.meevu.net') ||
      licenseUrl.includes('uniqcast.cryptoguard.com')
    );
  }

  private async getFairplayCertificate(
    serverCertificatePath: string
  ): Promise<ArrayBuffer> {
    try {
      this.logger.log(`Loading certificate at ${serverCertificatePath}`);
      const response = await fetch(serverCertificatePath);
      console.log('got cert response', response);
      const cert = await response.arrayBuffer();
      console.log('cert', cert);
      return cert;
    } catch (e) {
      this.logger.error(
        `Could not load certificate at ${serverCertificatePath}`
      );
      throw e;
    }
  }

  private async getLicenseResponse(
    licenseUrl: string,
    authorization: string,
    assetId: string,
    spc: Iterable<number>
  ): Promise<Uint8Array> {
    this.logger.log('getLicenseResponse', assetId);
    const headers = new Headers({
      'Content-type': 'application/json',
    });
    if (!this.bypassAuthentication(licenseUrl)) {
      headers.set('Authorization', authorization);
    }
    const licenseResponse = await fetch(licenseUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        assetId,
        spc: base64EncodeUint8Array(new Uint8Array(spc)),
      }),
    });
    const license = await licenseResponse.json();
    console.log('license', license);
    return base64DecodeUint8Array(license.ckc);
  }

  private async getFairplayMediaKeys(
    initDataType: string,
    certificateUrl: string
  ): Promise<MediaKeys> {
    this.logger.log('Creating MediaKeys');
    const access = await navigator.requestMediaKeySystemAccess(
      'com.apple.fps',
      [
        {
          initDataTypes: [initDataType],
          videoCapabilities: [
            {
              contentType: 'application/vnd.apple.mpegurl',
              robustness: '',
            },
          ],
          distinctiveIdentifier: 'not-allowed',
          persistentState: 'not-allowed',
          sessionTypes: ['temporary'],
        },
      ]
    );

    const keys = await access.createMediaKeys();
    console.log('mediakeys', keys);
    return keys;
  }

  async playFairplay(url: string, drm: Drm): Promise<void> {
    try {
      await this.player!.setMediaKeys(null);
      await this.playClearStream(url);
      this.encryptedEventListener = async (event) => {
        try {
          this.logger.log('encrypted event', event);
          const initDataType = event.initDataType;
          if (initDataType !== 'skd') {
            this.logger.error(
              `Received unexpected initialization data type "${initDataType}"`
            );
            return;
          }

          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          if (!this.player!.mediaKeys) {
            const keys = await this.getFairplayMediaKeys(
              initDataType,
              drm.data.certificateUrl!
            );
            await this.player!.setMediaKeys(keys);
            const cert = await this.getFairplayCertificate(
              drm.data.certificateUrl!
            );
            await keys.setServerCertificate(cert);
          }

          console.log('player media keys', this.player!.mediaKeys);
          const session = this.player!.mediaKeys!.createSession();
          const initData = event.initData;

          const keyURI = uInt8ArrayToString(new Uint8Array(initData as any));
          console.log('generating request, initData Key URI:', keyURI);
          await session.generateRequest(initDataType, initData as any);
          const messageEvent = await waitFor(session, 'message');
          console.log('messageEvent response', messageEvent);

          const response = await this.getLicenseResponse(
            drm.data.licenseUrl,
            drm.headers.Authorization,
            keyURI,
            messageEvent.message
          );

          const buf: ArrayBuffer = response.slice().buffer;
          await session.update(buf);
          console.log('session updated', keyURI);
        } catch (e) {
          console.log(e);
          this.logger.error('error at encrypted event', e);
        }
      };

      this.logger.log('adding encrypted event listener');
      this.player!.addEventListener('encrypted', this.encryptedEventListener);
    } catch (e) {
      this.logger.error('error at playFairplay', e);
    }
  }

  private getHLSWidevineConfig(drm: Drm): Partial<HlsConfig> {
    const licenseUrl = this.getLicenseUrl(drm);

    return {
      drmSystems: {
        'com.widevine.alpha': {
          licenseUrl,
        },
      },
      emeEnabled: true,
      debug: true,
      startPosition: 0,
      liveSyncDurationCount: 0,
    };
  }

  private getDashWidevineConfig(drm: Drm): dashjs.ProtectionDataSet {
    const licenseUrl = this.getLicenseUrl(drm);

    return {
      'com.widevine.alpha': {
        serverURL: licenseUrl,
      },
    };
  }

  async setHlsSource(
    url: string,
    drm: Drm | undefined,
    sourceId: number
  ): Promise<void> {
    this.logger.log('html5 with hls.js');
    let config;

    if (drm?.type === 'widevine') {
      config = this.getHLSWidevineConfig(drm);

      this.logger.log(`config: ${JSON.stringify(config)}`);
    } else {
      config = {
        debug: true,
        emeEnabled: true,
        startPosition: 0,
        liveSyncDurationCount: 0,
      };
    }

    const hls = new Hls(config);
    this.currentHlsInstance = hls;
    this.currentHlsInstance.loadSource(url);
    this.currentHlsInstance.attachMedia(this.player!);
    this.hlsSourceUrl = url;

    await new Promise((resolve, reject) => {
      this.currentHlsInstance?.on(
        Hls.Events.MANIFEST_PARSED,
        (_event, data) => {
          if (this.sourceId !== sourceId) {
            this.logger.log('MANIFEST_PARSED: stale source, ignoring');
            reject(new Error('Stale source'));
            return;
          }
          if (this.isDestroyed || this.player == null) {
            this.logger.log('player destroyed before manifest parsed');
            // Capture reference and null out immediately
            const hlsToDestroy = this.currentHlsInstance;
            this.currentHlsInstance = null;
            this.isDestroyed = true;
            this.isPlayerReady = false;
            // Defer destroy to avoid crashing HLS.js's internal event emission loop
            if (hlsToDestroy != null) {
              setTimeout(() => {
                try {
                  hlsToDestroy.destroy();
                } catch (e) {
                  // Ignore - instance already partially destroyed
                }
              }, 0);
            }
            reject(new Error('Player destroyed'));
            return;
          }

          this.logger.log('HLS.js manifest parsed', data);
          // Reset recovery state on successful manifest parse
          this.recoveryAttempts = 0;
          this.isRecoveringFromCorruptedChunk = false;

          if (data.audioTracks.length > 0) {
            this.audioTracks = data.audioTracks;
            this.logger.log('Audio Tracks:', this.audioTracks);
          } else {
            this.logger.log('No audio tracks found.');
            // Handle the absence of audio tracks if needed
          }

          if (data.subtitleTracks.length > 0) {
            this.subtitleTracks = data.subtitleTracks;
            this.logger.log('Subtitle Tracks:', this.subtitleTracks);
          } else {
            this.logger.log('No subtitle tracks found.');
            // Handle the absence of subtitle tracks if needed
          }

          const levels = hls.levels;
          this.currentHlsInstance?.on(
            Hls.Events.LEVEL_SWITCHED,
            (event, data) => {
              const currentLevel = data.level;

              this.eventCallback?.({
                key: this.playerId,
                type: 'updateVideoSize',
                bitrate: levels[currentLevel].bitrate,
                size: {
                  width: levels[currentLevel].width,
                  height: levels[currentLevel].height,
                },
              });
            }
          );
          resolve(data);
        }
      );

      this.currentHlsInstance?.on(Hls.Events.ERROR, (_event, data) => {
        if (this.sourceId !== sourceId) {
          this.logger.log(
            `HLS.js error for stale source ignored type=${data.type} details=${
              data.details
            } fatal=${String(data.fatal)}`
          );
          return;
        }
        this.logger.log(
          `HLS.js error type=${data.type} details=${
            data.details
          } fatal=${String(data.fatal)}`,
          data
        );

        if (data.response?.code === 429) {
          this.eventCallback?.({ key: this.playerId, type: 'error' });
        }

        if (data.type === 'networkError' && data.fatal) {
          reject(data);
          this.eventCallback?.({ key: this.playerId, type: 'error' });
          return;
        }

        if (data.details === Hls.ErrorDetails.BUFFER_SEEK_OVER_HOLE) {
          this.logger.log(
            'Ignoring non-fatal bufferSeekOverHole; letting hls.js handle the gap'
          );
          return;
        }

        if (
          data.details === Hls.ErrorDetails.BUFFER_APPENDING_ERROR ||
          data.details === Hls.ErrorDetails.FRAG_PARSING_ERROR
        ) {
          reject(data);
          this.hasBufferAppendingError = true;

          if (
            this.recoveryAttempts < this.maxRecoveryAttempts &&
            !this.isRecoveringFromCorruptedChunk
          ) {
            this.isRecoveringFromCorruptedChunk = true;
            this.recoveryAttempts++;

            this.logger.log(
              `Corrupted chunk detected (attempt ${this.recoveryAttempts}/${this.maxRecoveryAttempts}). Pausing stream...`
            );

            // Pause the player
            this.player?.pause();

            // Attempt recovery after a brief pause
            this.recoveryTimerHandle = setTimeout(() => {
              this.recoveryTimerHandle = null;
              if (this.sourceId !== sourceId) {
                this.logger.log(
                  'Recovery timer fired for stale source, skipping'
                );
                return;
              }
              this.logger.log('Attempting to recover from corrupted chunk...');
              hls.recoverMediaError();

              // Resume playback after recovery attempt
              this.recoveryResumeTimerHandle = setTimeout(() => {
                this.recoveryResumeTimerHandle = null;
                if (this.sourceId !== sourceId) {
                  this.logger.log(
                    'Recovery resume timer fired for stale source, skipping'
                  );
                  return;
                }
                if (this.player != null && !this.isPaused) {
                  this.player.play().catch((e) => {
                    this.logger.error(
                      'Error resuming playback after recovery:',
                      e
                    );
                  });
                }
                this.isRecoveringFromCorruptedChunk = false;
              }, 500);
            }, 100);
          } else if (this.recoveryAttempts >= this.maxRecoveryAttempts) {
            this.logger.error(
              'Max recovery attempts reached. Stopping playback.'
            );
            this.eventCallback?.({ key: this.playerId, type: 'error' });
            this.isRecoveringFromCorruptedChunk = false;
          }
        } else if (data.fatal && data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          reject(data);
          this.logger.log('Fatal media error detected, attempting recovery');
          try {
            hls.recoverMediaError();
          } catch (e) {
            this.logger.error('Fatal media error recovery failed:', e);
            this.eventCallback?.({ key: this.playerId, type: 'error' });
          }
        } else if (data.fatal) {
          reject(data);
          this.eventCallback?.({ key: this.playerId, type: 'error' });
        } else {
          this.hasBufferAppendingError = false;
        }
      });
    });
  }

  async setDashSource(url: string, drm: Drm | undefined): Promise<void> {
    this.logger.log('html5 with dash.js');

    const dash = dashjs.MediaPlayer().create();

    if (drm?.type === 'widevine') {
      dash.setProtectionData(this.getDashWidevineConfig(drm));
    }
    const isCutv = url.includes('begin=');
    const settings = {
      streaming: {
        delay: {
          useSuggestedPresentationDelay: true,
        },
        capabilities: {
          filterUnsupportedEssentialProperties: false,
        },
        liveCatchup: {
          enabled: !isCutv,
        },
      },
    };


    const streamReady = isCutv
      ? new Promise<void>((resolve, reject) => {
          let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

          const cleanup = (): void => {
            dash.off(
              dashjs.MediaPlayer.events.PLAYBACK_LOADED_DATA,
              onLoadedData
            );
            if (timeoutHandle != null) {
              clearTimeout(timeoutHandle);
            }
          };

          const onLoadedData = (): void => {
            cleanup();
            this.logger.log('CUTV DASH stream ready for initial seek');
            resolve();
          };

          timeoutHandle = setTimeout(() => {
            cleanup();
            reject(new Error('Timed out waiting for CUTV DASH stream readiness'));
          }, 12000);

          dash.on(
            dashjs.MediaPlayer.events.PLAYBACK_LOADED_DATA,
            onLoadedData
          );
        })
      : null;

    this.currentDashInstance = dash;
    this.currentDashInstance.on('error', (event) => {
      this.logger.error('dash.js error', event);
      this.eventCallback?.({ key: this.playerId, type: 'error' });
    });

    dash.initialize(this.player, url, true, isCutv ? 0 : undefined);
    if (this.ttmlRenderingDiv != null) {
      dash.attachTTMLRenderingDiv(this.ttmlRenderingDiv);
    }
    dash.updateSettings(settings);

    await streamReady;
  }

  async setSrc(
    url: string,
    drm: Drm | undefined,
    useSmartLib: boolean
  ): Promise<void> {
    const currentSourceId = ++this.sourceId;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    this.logger.log(
      `setSrc: ${url}  drm: ${JSON.stringify(drm)} useSmartLib: ${
        useSmartLib ? 'true' : 'false'
      }`
    );

    if (this.player == null || this.isDestroyed) {
      this.logger.error('Player not found or initialized');
      await this.init();
      if (this.sourceId !== currentSourceId) {
        this.logger.log('setSrc aborted after init for stale source');
        return;
      }

      // Check again after init
      if (this.player == null) {
        throw new Error('Failed to initialize player - element not found');
      }
    }

    this.isDestroyed = false;

    try {
      this.clearCurrentSource();

      if (useSmartLib) {
        this.session = SmartLib.getInstance().createStreamingSession();
        try {
          const result = await this.session.getURL(url);
          this.logger.log('SmartLib session getURL resolved');
          if (this.sourceId !== currentSourceId) {
            this.logger.log('SmartLib result ignored for stale source');
            this.stopSession();
            return;
          }
          if (!(result.isError() as boolean)) {
            url = result.getURL();
            this.logger.log('SmartLib URL applied');
          } else {
            this.stopSession();
            this.logger.error('SmartLib session had an error');
          }
        } catch (error: any) {
          this.logger.error('SmartLibError during session creation', error);
          this.stopSession();
        }
      }

      const isHLS = url.includes('.m3u8');
      const isDash = url.includes('.mpd');

      if (this.sourceId !== currentSourceId) {
        this.logger.log('setSrc aborted before source attach for stale source');
        return;
      }

      if (drm?.type === 'fairplay') {
        await this.playFairplay(url, drm);
      } else if (isHLS) {
        await this.setHlsSource(url, drm, currentSourceId);
      } else if (isDash) {
        await this.setDashSource(url, drm);
      } else {
        await this.playClearStream(url);
      }
    } catch (e: any) {
      this.logger.log('error at loading source', e);
      throw new Error(e.toString());
    }
  }

  async pause(): Promise<void> {
    this.logger.log('[Player pause]');
    if (this.player == null || this.isDestroyed) {
      this.logger.log('[Player pause] Player not available');
      return;
    }
    try {
      if (!this.isPaused) {
        this.player.pause();
        this.isPaused = true;
      }
    } catch (e) {
      this.logger.error('Error pausing player:', e);
    }
  }

  async play(): Promise<void> {
    this.logger.log('[Player play]');
    if (this.player == null || this.isDestroyed) {
      this.logger.log('[Player play] Player not available');
      return;
    }
    try {
      this.isPaused = false;
      await this.player.play();
    } catch (e) {
      this.logger.error('Error playing:', e);
    }
  }

  async stop(): Promise<void> {
    this.logger.log('[Player stop]');
    this.isPlayerReady = false;
    html5PlayerInitialized = false;
    this.stopSession();
    await this.pause();
    this.isDestroyed = true;
    this.player = undefined;

    try {
      if (this.currentHlsInstance != null) {
        const hlsToDestroy = this.currentHlsInstance;
        this.currentHlsInstance = null;
        // Defer destroy to avoid issues if called during HLS event processing
        setTimeout(() => {
          try {
            hlsToDestroy.destroy();
          } catch (e) {
            // Ignore - instance may already be destroyed
          }
        }, 0);
      }
    } catch (e) {
      this.logger.error('Error destroying HLS instance:', e);
      this.currentHlsInstance = null;
    }

    try {
      if (this.currentDashInstance != null) {
        this.currentDashInstance.reset();
        this.currentDashInstance = null;
      }
    } catch (e) {
      this.logger.error('Error resetting DASH instance:', e);
    }
  }

  getDurationInMilliseconds(): number {
    if (this.player == null) {
      return 0;
    }
    return this.player.duration * 1000;
  }

  async position(): Promise<number> {
    if (this.player == null || this.isDestroyed || !this.isPlayerReady) {
      if (this.player == null) {
        this.logger.error('Player is null');
      }
      return 0;
    }

    try {
      const currentTime =
        (this.currentDashInstance?.time() ?? this.player.currentTime) * 1000;

      this.logger.log(`position ${currentTime}`);
      return currentTime;
    } catch (e) {
      this.logger.error('Error getting position:', e);
      return 0;
    }
  }

  async seekTo(positionInMSec: number): Promise<void> {
    this.logger.log(`seekTo ${positionInMSec}`);

    let positionInSec = 0;
    if (positionInMSec < 0) {
      this.logger.log('Error setting position to negative');
      return;
    }
    positionInSec = Math.round(positionInMSec / 1000);

    if (positionInMSec > 0 && positionInSec === 0) {
      positionInSec = 1;
    }

    if (this.player == null || this.isDestroyed || !this.isPlayerReady) {
      this.logger.error('Player is null or not ready');
      return;
    }

    try {
      if (this.currentDashInstance != null) {
        this.currentDashInstance.seek(positionInSec);
        return;
      }
      this.player.currentTime = positionInSec;
    } catch (e) {
      this.logger.error('Error seeking:', e);
    }
  }

  async setAudioTrack(
    index: number,
    language: string,
    languageCode: string
  ): Promise<void> {
    this.logger.log('[Player setAudioTrack] language: ', language);

    if (this.player == null || this.isDestroyed) {
      this.logger.log('Player not available for audio track change');
      return;
    }

    if (this.currentAudioTrackIndex === index) {
      this.logger.log('Audio track already selected: ', index);
      return;
    }

    if (
      this.currentHlsInstance != null &&
      !this.isDestroyed &&
      this.currentHlsInstance.media != null
    ) {
      try {
        this.currentHlsInstance.audioTrack = index;
        this.currentAudioTrackIndex = index;

        if (
          this.hlsSourceUrl !== null &&
          this.hlsSourceUrl !== '' &&
          this.hasBufferAppendingError
        ) {
          this.currentHlsInstance.loadSource(this.hlsSourceUrl);
          this.currentHlsInstance.attachMedia(this.player);
        }
      } catch (e) {
        this.logger.error('Error while setting audio track: ', e);
      }
    } else if (this.currentDashInstance != null) {
      try {
        const tracks = this.currentDashInstance.getTracksFor('audio');
        if (index >= 0 && index < tracks.length) {
          this.currentDashInstance.setCurrentTrack(tracks[index]);
          this.currentAudioTrackIndex = index;
        } else {
          const trackIndex = tracks.findIndex(
            (t: any) => t.lang === language || t.lang === languageCode
          );
          if (trackIndex !== -1) {
            this.currentDashInstance.setCurrentTrack(tracks[trackIndex]);
            this.currentAudioTrackIndex = trackIndex;
          }
        }
      } catch (e) {
        this.logger.error('Error while setting DASH audio track: ', e);
      }
    } else if ((this.player as any).audioTracks != null) {
      // audioTracks property only supported via safari
      for (let i = 0; i < (this.player as any).audioTracks.length; i++) {
        const track = (this.player as any).audioTracks[i];
        if (track.label === language) {
          track.enabled = true;
          this.currentAudioTrackIndex = i;
        } else {
          track.enabled = false;
        }
      }
    }
  }

  async setSubtitleTrack(index: number, language: String): Promise<void> {
    this.logger.log('[Player setSubtitleTrack] language: ', language);

    if (this.player == null) {
      return;
    }
    if (this.currentHlsInstance != null) {
      if (index === -1) {
        // No subtitle option
        this.logger.log('[Player setSubtitleTrack] No subtitle option');
        this.currentHlsInstance.subtitleDisplay = false;
        return;
      }
      for (const track of this.subtitleTracks) {
        if (track.id === index) {
          this.logger.log('[Player setSubtitleTrack]', track.lang);
          this.currentHlsInstance.subtitleDisplay = true;
          this.currentHlsInstance.subtitleTrack = track.id;
          break;
        }
      }
    } else if (this.currentDashInstance != null) {
      if (index === -1) {
        this.currentDashInstance.setTextTrack(-1);
        return;
      }
      const tracks = this.currentDashInstance.getTracksFor('text');
      const trackIndex = tracks.findIndex(
        (t: any) => t.lang === language || tracks.indexOf(t) === index
      );
      if (trackIndex !== -1) {
        this.currentDashInstance.setTextTrack(trackIndex);
      }
    } else {
      for (let i = 0; i < this.player.textTracks.length; i++) {
        const track = this.player.textTracks[i];
        if (track.label === language) {
          track.mode = 'showing';
        } else {
          track.mode = 'disabled';
        }
      }
    }
  }

  async getSubtitleTracks(): Promise<SubtitleTrack[]> {
    this.logger.log('[Player getSubtitleTracks]');
    if (this.player == null) {
      return [];
    }

    const subtitleTracks: SubtitleTrack[] = [];
    if (this.subtitleTracks.length > 0) {
      for (let i = 0; i < this.subtitleTracks.length; i++) {
        const track = this.subtitleTracks[i];
        const subtitleTrack: SubtitleTrack = {
          index: track.id,
          language: track.lang ?? track.name ?? track.id ?? 'unknown',
        };

        subtitleTracks.push(subtitleTrack);
      }

      return subtitleTracks;
    }

    if (this.currentDashInstance != null) {
      const tracks = this.currentDashInstance.getTracksFor('text');
      for (let i = 0; i < tracks.length; i++) {
        subtitleTracks.push({
          index: i,
          language: (tracks[i] as any).lang ?? 'unknown',
        });
      }
      return subtitleTracks;
    }

    for (let i = 0; i < this.player?.textTracks.length; i++) {
      const track = this.player.textTracks[i];
      const subtitleTrack: SubtitleTrack = {
        index: i,
        language: track.language ?? track.label ?? track.id ?? 'unknown',
      };

      subtitleTracks.push(subtitleTrack);
    }

    return subtitleTracks;
  }

  async setQuality(
    bitrate?: number,
    width?: number,
    height?: number
  ): Promise<void> {
    if (
      width === undefined ||
      height === undefined ||
      width === 0 ||
      height === 0 ||
      isNaN(width) ||
      isNaN(height)
    ) {
      return;
    }
    this.logger.log(`[Player setQuality] width: ${width} height: ${height}`);
    if (this.currentHlsInstance != null) {
      const levels = this.currentHlsInstance.levels;
      const selectedLevelIndex = levels.findIndex(
        (level) => level.width === width && level.height === height
      );
      this.currentHlsInstance.currentLevel = selectedLevelIndex;
    }

    if (this.currentDashInstance != null) {
      const bitrates = this.currentDashInstance.getBitrateInfoListFor('video');
      const selectedBitrateIndex = bitrates.findIndex(
        (b) => b.width === width && b.height === height
      );
      if (selectedBitrateIndex === -1) {
        this.currentDashInstance.updateSettings({
          streaming: {
            abr: {
              autoSwitchBitrate: { video: true },
            },
          },
        });
        return;
      }
      this.currentDashInstance.updateSettings({
        streaming: {
          abr: {
            autoSwitchBitrate: { video: false },
          },
        },
      });
      this.currentDashInstance.setQualityFor(
        'video',
        selectedBitrateIndex,
        true
      );
    }
  }

  async setVolume(volume: number): Promise<void> {
    this.logger.log('[Player setVolume]');
    if (this.player == null) {
      return;
    }
    this.player.volume = volume;
  }

  onEvent(listener: PlayerEventCallback): void {
    this.logger.log('[Player onEvent]');
    this.eventCallback = listener;
  }
}

export { Html5Player };
